import { useEffect, useState } from 'react';
import type { ApiClient, Haus, Modell } from '../api';
import { hausText, istMehrhaus, naechsteNummer, quellenWarnung } from '../mehrhaus';

/**
 * F60 H2b (PLAN-MEHRHAUS-20261026 §8): Häuser eines Projekts pflegen.
 *
 * Liste mit Nummer, Kürzel, Name, Standard und Aktiv; „Haus anlegen“ nimmt die nächste freie Nummer. Das Standardhaus
 * lässt sich nicht stilllegen (der Server lehnt es ebenso ab). Mehr als eine Raum- oder Türbuchquelle je Haus bleibt
 * erlaubt (Bauabschnitte), wird aber als Warnung gezeigt. Ohne Häuser (ältere API) zeigt der Abschnitt nur den Hinweis,
 * dass alles zu Haus 01 gehört.
 */
export function Hauspflege({ api, projektId, haeuser, neuLaden }: {
  api: ApiClient;
  projektId: string;
  haeuser: Haus[];
  neuLaden: () => void;
}) {
  const [laeuft, setLaeuft] = useState<number | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const liste = [...haeuser].sort((a, b) => a.reihenfolge - b.reihenfolge || a.nummer - b.nummer);
  const neu = naechsteNummer(haeuser);

  const setzen = async (nummer: number, daten: Parameters<ApiClient['hausSetzen']>[2]) => {
    setLaeuft(nummer);
    setFehler(null);
    try {
      await api.hausSetzen(projektId, nummer, daten);
      neuLaden();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(null);
    }
  };

  return (
    <section className="karte" aria-label="Häuser">
      <h2 className="karte-titel">Häuser</h2>
      <p style={{ marginTop: 0 }} className="schwach">
        Ein Projekt kann mehrere Häuser führen (Haus 01, Haus 02 …). Ohne Zuordnung gehört alles zum Standardhaus.
        Ab zwei aktiven Häusern gibt es die Haus-Wahl und die Spalte „Haus“ in Raumbuch und Türbuch.
      </p>

      {fehler && (
        <div className="hinweis fehler">
          <span className="hinweis-zeichen">!</span>{fehler}
        </div>
      )}

      {liste.length === 0 ? (
        <p className="schwach">Noch kein Haus angelegt – alles gehört zu Haus 01.</p>
      ) : (
        <div className="tabellenrahmen">
          <table className="zuordnung">
            <thead>
              <tr>
                <th>Haus</th>
                <th>Kürzel</th>
                <th>Name</th>
                <th>Standard</th>
                <th>Aktiv</th>
                <th>Modelle</th>
                <th>Quellen</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {liste.map((h) => (
                <Hauszeile
                  key={h.id}
                  haus={h}
                  laeuft={laeuft === h.nummer}
                  setzen={(daten) => setzen(h.nummer, daten)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {neu != null && (
        <button
          type="button"
          style={{ marginTop: 10 }}
          disabled={laeuft != null}
          onClick={() => setzen(neu, {})}
        >
          {hausText(neu)} anlegen
        </button>
      )}
    </section>
  );
}

function Hauszeile({ haus, laeuft, setzen }: {
  haus: Haus;
  laeuft: boolean;
  setzen: (daten: Parameters<ApiClient['hausSetzen']>[2]) => void;
}) {
  const [kuerzel, setKuerzel] = useState(haus.kuerzel);
  const [name, setName] = useState(haus.name);
  useEffect(() => { setKuerzel(haus.kuerzel); setName(haus.name); }, [haus.kuerzel, haus.name]);

  const geaendert = kuerzel.trim() !== haus.kuerzel || name.trim() !== haus.name;
  const warnung = quellenWarnung(haus);

  return (
    <tr>
      <td className="zuordnung-name">{hausText(haus.nummer)}</td>
      <td>
        <input
          value={kuerzel}
          maxLength={8}
          size={6}
          onChange={(e) => setKuerzel(e.target.value)}
          aria-label={`${hausText(haus.nummer)}: Kürzel`}
        />
      </td>
      <td>
        <input
          value={name}
          maxLength={120}
          onChange={(e) => setName(e.target.value)}
          aria-label={`${hausText(haus.nummer)}: Name`}
        />
      </td>
      <td style={{ textAlign: 'center' }}>
        <input
          type="radio"
          name="standardhaus"
          checked={haus.istStandard}
          disabled={laeuft || haus.istStandard || !haus.isActive}
          onChange={() => setzen({ istStandard: true })}
          aria-label={`${hausText(haus.nummer)}: Standardhaus`}
        />
      </td>
      <td style={{ textAlign: 'center' }}>
        <input
          type="checkbox"
          checked={haus.isActive}
          disabled={laeuft || haus.istStandard}
          title={haus.istStandard ? 'Das Standardhaus kann nicht stillgelegt werden.' : undefined}
          onChange={() => setzen({ isActive: !haus.isActive })}
          aria-label={`${hausText(haus.nummer)}: aktiv`}
        />
      </td>
      <td style={{ textAlign: 'right' }}>{haus.modelle}</td>
      <td>
        {warnung
          ? <span className="hinweis-text warnung" title="Erlaubt (Bauabschnitte), bitte prüfen.">{warnung}</span>
          : <span className="schwach">{haus.raumbuchquellen} / {haus.tuerbuchquellen}</span>}
      </td>
      <td>
        <button
          type="button"
          disabled={laeuft || !geaendert || !kuerzel.trim() || !name.trim()}
          onClick={() => setzen({ kuerzel: kuerzel.trim(), name: name.trim() })}
        >
          Speichern
        </button>
      </td>
    </tr>
  );
}

/**
 * F60 H2b: Auswahl des Hauses eines Modells (Spalte „Haus“ der Modellzuordnung). Erst ab zwei aktiven Häusern; leer =
 * Standardhaus. Ein stillgelegtes Haus ist nur dann wählbar, wenn das Modell schon darin liegt (Anzeige).
 */
export function ModellHaus({ modell, haeuser, laeuft, setzen }: {
  modell: Modell;
  haeuser: Haus[];
  laeuft: boolean;
  setzen: (hausId: string | null) => void;
}) {
  if (!istMehrhaus(haeuser)) return null;
  const standard = haeuser.find((h) => h.istStandard);
  // Ausdrücklich auf das Standardhaus gesetzt zählt wie leer (NULL = Standardhaus).
  const wahl = modell.hausId && modell.hausId !== standard?.id ? modell.hausId : '';
  const angeboten = haeuser
    .filter((h) => h.isActive || h.id === wahl)
    .sort((a, b) => a.reihenfolge - b.reihenfolge || a.nummer - b.nummer);

  return (
    <select
      value={wahl}
      disabled={laeuft}
      onChange={(e) => setzen(e.target.value === '' ? null : e.target.value)}
      aria-label={`${modell.name}: Haus`}
    >
      <option value="">{standard ? `${hausText(standard.nummer)} (Standard)` : 'Standardhaus'}</option>
      {angeboten.filter((h) => !h.istStandard).map((h) => (
        <option key={h.id} value={h.id}>{hausText(h.nummer)}</option>
      ))}
    </select>
  );
}
